/* ────────────────────────────────────────────────────────────────────
   Cart drawer — Dahabeya
   Hydrates the slide-in panel from /cart.js, handles qty changes via
   /cart/change.js, listens on window for cart:open / cart:updated.
   Locale strings come from data attributes on [data-cart-root] so the
   JS stays language-agnostic.
   ──────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  var root = document.querySelector('[data-cart-root]');
  if (!root) return;

  var panel = root.querySelector('[data-cart-drawer]');
  var backdrop = root.querySelector('[data-cart-backdrop]');
  var itemsEl = root.querySelector('[data-cart-items]');
  var emptyEl = root.querySelector('[data-cart-empty]');
  var footerEl = root.querySelector('[data-cart-footer]');
  var subtotalEl = root.querySelector('[data-cart-subtotal]');
  var totalEl = root.querySelector('[data-cart-total]');

  // Locale strings (passed via data attributes from Liquid)
  var L = {
    quantity: root.dataset.lQuantity || 'Quantity',
    remove: root.dataset.lRemove || 'Remove',
    decrease: root.dataset.lDecrease || 'Decrease quantity',
    increase: root.dataset.lIncrease || 'Increase quantity',
    altImage: root.dataset.lAltImage || ''
  };
  var currencySymbol = root.dataset.currencySymbol || 'ج.م';
  var routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Free shipping progress bar + shipping cost — values resolved at the section root.
  // Both threshold and shipping_value are stored in cents (Liquid multiplies the
  // EGP setting by 100) so all arithmetic stays in the same unit as cart.*_price.
  var freeShippingThreshold = parseInt(root.dataset.freeShippingThreshold, 10) || 0;
  var shippingValue = parseInt(root.dataset.shippingValue, 10) || 0;
  var shippingMessageTpl = root.dataset.lShippingMessage || '';
  var shippingQualifiedText = root.dataset.lShippingQualified || '';
  var shippingFreeText = root.dataset.lShippingFree || 'Free';
  var shippingBar = root.querySelector('[data-shipping-bar]');
  var shippingFill = root.querySelector('[data-shipping-fill]');
  var shippingMessage = root.querySelector('[data-shipping-message]');
  var shippingTrack = root.querySelector('[data-shipping-track]');
  var shippingEl = root.querySelector('[data-cart-shipping]');

  var arabic = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  var useArabicNumerals = document.documentElement.lang === 'ar';

  function toLocaleDigits(str) {
    if (!useArabicNumerals) return String(str);
    return String(str).replace(/\d/g, function (d) {
      return arabic[+d];
    });
  }

  function parseLocaleDigits(str) {
    return String(str).replace(/[٠-٩]/g, function (d) {
      return arabic.indexOf(d);
    });
  }

  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    var parts = amount.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return toLocaleDigits(parts.join('.')) + ' ' + currencySymbol;
  }

  function sizedImageUrl(src, width) {
    if (!src) return '';
    // Shopify CDN: insert _200x. before extension (handles query strings).
    var match = src.match(/^([^?]+)(\?.*)?$/);
    if (!match) return src;
    var path = match[1];
    var query = match[2] || '';
    var sized = path.replace(/\.(jpe?g|png|gif|webp|avif)(?=$)/i, '_' + width + 'x.$1');
    return sized + query;
  }

  function buildItem(item) {
    var li = document.createElement('li');
    li.className = 'cart-drawer__item';
    li.setAttribute('data-line-key', item.key);

    // Thumb wrapper + <img> (gradient shows through if image missing)
    var thumb = document.createElement('div');
    thumb.className = 'cart-drawer__item-thumb';
    if (item.image) {
      var src = sizedImageUrl(item.image, 200);
      if (/^https?:\/\//i.test(src) || src.charAt(0) === '/') {
        var img = document.createElement('img');
        img.className = 'cart-drawer__item-thumb-img';
        img.src = src;
        img.width = 64;
        img.height = 64;
        img.loading = 'lazy';
        img.alt = item.product_title || L.altImage;
        thumb.appendChild(img);
      }
    }
    li.appendChild(thumb);

    // Info
    var info = document.createElement('div');
    info.className = 'cart-drawer__item-info';

    var title = document.createElement('p');
    title.className = 'cart-drawer__item-title';
    title.textContent = item.product_title;
    info.appendChild(title);

    if (item.variant_title && item.variant_title !== 'Default Title') {
      var vt = document.createElement('p');
      vt.className = 'cart-drawer__item-variant';
      vt.textContent = item.variant_title;
      info.appendChild(vt);
    }

    // Qty pill
    var qtyWrap = document.createElement('div');
    qtyWrap.className = 'cart-drawer__item-qty';
    qtyWrap.setAttribute('role', 'group');
    qtyWrap.setAttribute('aria-label', L.quantity);

    var dec = document.createElement('button');
    dec.type = 'button';
    dec.className = 'cart-drawer__qty-btn';
    dec.setAttribute('data-qty-decrement', '');
    dec.setAttribute('aria-label', L.decrease);
    dec.textContent = '−';

    var qtyVal = document.createElement('span');
    qtyVal.className = 'cart-drawer__qty-value';
    qtyVal.textContent = toLocaleDigits(item.quantity);

    var inc = document.createElement('button');
    inc.type = 'button';
    inc.className = 'cart-drawer__qty-btn';
    inc.setAttribute('data-qty-increment', '');
    inc.setAttribute('aria-label', L.increase);
    inc.textContent = '+';

    qtyWrap.appendChild(dec);
    qtyWrap.appendChild(qtyVal);
    qtyWrap.appendChild(inc);
    info.appendChild(qtyWrap);

    li.appendChild(info);

    // End column (price + remove)
    var end = document.createElement('div');
    end.className = 'cart-drawer__item-end';

    var price = document.createElement('span');
    price.className = 'cart-drawer__item-price';
    price.textContent = formatMoney(item.final_line_price);
    end.appendChild(price);

    var remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'cart-drawer__item-remove';
    remove.setAttribute('data-qty-remove', '');
    remove.textContent = L.remove;
    end.appendChild(remove);

    li.appendChild(end);
    return li;
  }

  // Build the "Add X more for free shipping" message by splitting on the
  // __AMOUNT__ placeholder and appending text/strong nodes. We avoid innerHTML
  // so the dynamic amount can never be misinterpreted as HTML.
  function setShippingMessage(template, amountText) {
    while (shippingMessage.firstChild) {
      shippingMessage.removeChild(shippingMessage.firstChild);
    }
    var parts = template.split('__AMOUNT__');
    shippingMessage.appendChild(document.createTextNode(parts[0]));
    if (parts.length > 1) {
      var strong = document.createElement('strong');
      strong.textContent = amountText;
      shippingMessage.appendChild(strong);
      shippingMessage.appendChild(document.createTextNode(parts[1] || ''));
    }
  }

  function renderShippingBar(cart) {
    if (!shippingBar || freeShippingThreshold <= 0) return;
    var subtotal = cart.items_subtotal_price || 0;
    if (!cart.item_count) {
      shippingBar.hidden = true;
      return;
    }
    shippingBar.hidden = false;
    var pct = Math.min(100, Math.round((subtotal / freeShippingThreshold) * 100));
    shippingFill.style.width = pct + '%';
    if (shippingTrack) shippingTrack.setAttribute('aria-valuenow', String(pct));
    if (subtotal >= freeShippingThreshold) {
      shippingBar.dataset.qualified = 'true';
      shippingMessage.textContent = shippingQualifiedText;
    } else {
      delete shippingBar.dataset.qualified;
      setShippingMessage(shippingMessageTpl, formatMoney(freeShippingThreshold - subtotal));
    }
  }

  function render(cart) {
    var count = cart.item_count || 0;

    // Header badges
    var badges = document.querySelectorAll('.section-header__cart-count');
    badges.forEach(function (b) {
      if (count > 0) {
        b.textContent = toLocaleDigits(count);
        b.hidden = false;
        b.style.display = '';
      } else {
        b.textContent = '';
        b.style.display = 'none';
      }
    });

    while (itemsEl.firstChild) itemsEl.removeChild(itemsEl.firstChild);

    if (count === 0) {
      emptyEl.hidden = false;
      footerEl.hidden = true;
      renderShippingBar(cart);
      return;
    }

    emptyEl.hidden = true;
    footerEl.hidden = false;

    var frag = document.createDocumentFragment();
    cart.items.forEach(function (item) {
      frag.appendChild(buildItem(item));
    });
    itemsEl.appendChild(frag);

    subtotalEl.textContent = formatMoney(cart.items_subtotal_price);

    // Effective shipping: 0 if the cart qualifies for free shipping or no
    // shipping value is configured; otherwise the merchant's configured value.
    // Total is the sum so the displayed numbers add up to what the shopper
    // expects to pay (final amount may differ at checkout based on Shopify's
    // shipping zones — this is an estimate, matching standard cart UX).
    var qualifiesForFree = freeShippingThreshold > 0 && cart.items_subtotal_price >= freeShippingThreshold;
    var effectiveShipping = (shippingValue > 0 && !qualifiesForFree) ? shippingValue : 0;
    if (shippingEl) {
      shippingEl.textContent = effectiveShipping > 0 ? formatMoney(effectiveShipping) : shippingFreeText;
    }
    totalEl.textContent = formatMoney(cart.total_price + effectiveShipping);

    renderShippingBar(cart);
  }

  function fetchCart() {
    return fetch(routeRoot + 'cart.js', { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        render(cart);
        return cart;
      })
      .catch(function (err) {
        console.error('Cart drawer fetch failed:', err);
      });
  }

  function updateLine(key, quantity) {
    return fetch(routeRoot + 'cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        render(cart);
        return cart;
      })
      .catch(function (err) {
        console.error('Cart drawer update failed:', err);
      });
  }

  // ─── Focus trap ────────────────────────────────────────────
  var lastFocusedBeforeOpen = null;

  function getFocusables() {
    return Array.prototype.filter.call(
      panel.querySelectorAll(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ),
      function (el) {
        return !el.hidden && el.offsetParent !== null;
      }
    );
  }

  function trapTab(e) {
    if (e.key !== 'Tab') return;
    var focusables = getFocusables();
    if (focusables.length === 0) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // ─── Open / close ──────────────────────────────────────────
  function openDrawer() {
    lastFocusedBeforeOpen = document.activeElement;
    root.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    window.setTimeout(function () {
      panel.focus();
    }, 50);
    panel.addEventListener('keydown', trapTab);
    fetchCart();
  }

  function closeDrawer() {
    root.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    panel.removeEventListener('keydown', trapTab);
    if (lastFocusedBeforeOpen && typeof lastFocusedBeforeOpen.focus === 'function') {
      lastFocusedBeforeOpen.focus();
    }
  }

  // ─── Event wiring ──────────────────────────────────────────
  backdrop.addEventListener('click', closeDrawer);

  root.querySelectorAll('[data-cart-close]').forEach(function (el) {
    el.addEventListener('click', closeDrawer);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && root.classList.contains('is-open')) {
      closeDrawer();
    }
  });

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-cart-trigger]');
    if (trigger) {
      e.preventDefault();
      openDrawer();
    }
  });

  itemsEl.addEventListener('click', function (e) {
    var line = e.target.closest('[data-line-key]');
    if (!line) return;
    var key = line.getAttribute('data-line-key');
    var qtyEl = line.querySelector('.cart-drawer__qty-value');
    var currentQty = 0;
    if (qtyEl) {
      currentQty = parseInt(parseLocaleDigits(qtyEl.textContent), 10) || 0;
    }

    if (e.target.closest('[data-qty-increment]')) {
      updateLine(key, currentQty + 1);
    } else if (e.target.closest('[data-qty-decrement]')) {
      updateLine(key, Math.max(0, currentQty - 1));
    } else if (e.target.closest('[data-qty-remove]')) {
      updateLine(key, 0);
    }
  });

  window.addEventListener('cart:open', openDrawer);
  window.addEventListener('cart:updated', fetchCart);

  window.DahabeyaCart = {
    open: openDrawer,
    close: closeDrawer,
    refresh: fetchCart
  };

  fetchCart();
})();
